import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, Phone, MessageCircle, ChevronDown, Building2, User2, Clock } from "lucide-react";
import useSEO from "@/lib/useSEO";

const ZONE_HUE = {
  North: "from-blue-50 to-slate-50 border-blue-300",
  South: "from-emerald-50 to-teal-50 border-emerald-300",
  East:  "from-amber-50 to-orange-50 border-amber-300",
  West:  "from-fuchsia-50 to-pink-50 border-fuchsia-300",
  Central:"from-indigo-50 to-slate-50 border-indigo-300",
};

// Normalise Indian numbers to E.164 for WhatsApp deep links
function waLink(num) {
  const d = (num || "").replace(/\D/g, "");
  if (!d) return null;
  const full = d.length === 10 ? `91${d}` : d;
  return `https://wa.me/${full}`;
}

export default function ExecutiveTeams() {
  const [zones, setZones] = useState([]);
  const [openZone, setOpenZone] = useState(null);
  useSEO({ title: "Our Executive Teams — Regional Leaders · Sarthi Associates", description: "Find your nearest Sarthi Associates regional leader. Executive teams organised by zone (North, South, East, West, Central) with direct call and WhatsApp.", path: "/executive-teams" });
  useEffect(() => {
    api.get("/executives").then((r) => {
      setZones(r.data || []);
      if ((r.data || []).length) setOpenZone(r.data[0].zone);
    }).catch(() => {});
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="executive-teams-page">
      <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-sarthi-accent px-4 py-1 text-xs font-bold uppercase tracking-wider text-sarthi-primary">Our Executive Teams</div>
      <h1 className="mt-3 font-display text-4xl font-extrabold text-sarthi-text sm:text-5xl">Regional leaders. On the ground with you.</h1>
      <p className="mt-3 max-w-3xl text-lg font-medium text-sarthi-muted">Reach out to your nearest zone's executive team directly. All numbers below are one tap away — call or WhatsApp instantly.</p>

      {/* Managing Director direct email — always visible above the zones */}
      <a
        href="mailto:cmd@sarthiassociates.com"
        data-testid="md-email-link"
        className="mt-5 inline-flex items-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-800 hover:bg-amber-100"
      >
        <span className="inline-block h-2 w-2 rounded-full bg-amber-500" />
        Office of the Managing Director · cmd@sarthiassociates.com
      </a>

      {zones.length === 0 && (
        <div className="mt-10 sarthi-card p-10 text-center text-sarthi-muted" data-testid="executive-teams-empty">Executive team details will be published shortly.</div>
      )}

      <div className="mt-8 space-y-4">
        {zones.map(({ zone, executives }) => {
          const isOpen = openZone === zone;
          return (
            <div key={zone} data-testid={`zone-${zone}`} className={`sarthi-card overflow-hidden bg-gradient-to-br ${ZONE_HUE[zone] || "from-stone-50 to-stone-100 border-sarthi-border"}`}>
              <button
                data-testid={`zone-toggle-${zone}`}
                onClick={() => setOpenZone(isOpen ? null : zone)}
                className="flex w-full items-center justify-between px-5 py-4 text-left"
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-sarthi-muted">Zone</div>
                  <div className="mt-1 font-display text-2xl font-extrabold text-sarthi-text">{zone} · <span className="text-sarthi-primary">{executives.length} leader{executives.length !== 1 && "s"}</span></div>
                </div>
                <ChevronDown className={`h-6 w-6 text-sarthi-secondary transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="grid grid-cols-1 gap-3 border-t-2 border-white/60 bg-white/60 p-4 md:grid-cols-2">
                  {executives.map((e) => {
                    const wa = waLink(e.whatsapp_number || e.contact_number);
                    return (
                      <div key={e.id} data-testid={`exec-${e.id}`} className="rounded-2xl border-2 border-sarthi-border bg-white p-5 shadow-sm">
                        <div className="flex items-start gap-3">
                          {e.photo_url ? (
                            <img src={e.photo_url} alt={e.name} className="h-14 w-14 rounded-full object-cover" />
                          ) : (
                            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sarthi-secondary text-white"><User2 className="h-6 w-6" /></div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-display text-lg font-extrabold leading-tight text-sarthi-text">{e.name}</div>
                            {e.designation && <div className="text-xs font-bold uppercase tracking-wide text-sarthi-primary">{e.designation}</div>}
                            {e.experience && <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sarthi-muted"><Clock className="h-3 w-3" /> {e.experience}</div>}
                          </div>
                        </div>
                        <div className="mt-3 flex items-start gap-2 text-sm text-sarthi-text"><Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-sarthi-secondary" /><span className="font-medium">{e.office_address}</span></div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <a data-testid={`call-${e.id}`} href={`tel:${e.contact_number}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-sarthi-primary px-3 py-2 text-sm font-bold text-white hover:brightness-110">
                            <Phone className="h-4 w-4" /> Call
                          </a>
                          {wa && (
                            <a data-testid={`wa-${e.id}`} href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2 text-sm font-bold text-white hover:brightness-110">
                              <MessageCircle className="h-4 w-4" /> WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
